'use client';
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import { useCookies } from 'next-client-cookies';
import { toast } from 'react-hot-toast';
import Link from 'components/commercetools-ui/atoms/link';
import Breadcrumb from 'components/commercetools-ui/molecules/breadcrumb';
import { NavLeftIcon } from 'components/icons';
import { AccountContext } from 'context/account';
import { classifyCartKind } from 'helpers/constants/cart';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import useHash from 'helpers/hooks/useHash';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import mapCosts from 'helpers/utils/mapCosts';
import { getAttributeLabel } from 'helpers/utils/attributes';
import { desktop } from 'helpers/utils/screensizes';
import { useRouter } from 'i18n/routing';
import { EU_LOCALE, getLocalizationInfo, NORTH_AMERICA_LOCALES } from 'project.config';
import { useFeatures } from 'providers/feature-flag';
import { usePageLoader } from 'providers/loader';
import { Wishlist } from 'types/entity/wishlist';
import { useCart } from 'frontastic';
import { useProjectSettings, useWishlist } from 'frontastic/hooks';
import ModalPopUp from '../modal-pop-up';
import AccountTabsComponent from './components/AccountTabs';
import OrderFulfillmentSection from './components/OrderFullmentSection';
import OrderItemsSection from './components/OrderItemsSection';
import OrderStepperComponent from './components/OrderStepper';
import OrderSummary from './components/OrderSummary';
import {
  getBackendCurrentIndex,
  getP2RBackendTimelineSteps,
  getTotalCount,
  groupLineItemsByOrderStatus,
  groupLineItemsWithTrackingAndStatusSplit,
  hasSameStatusCode,
  POSSIBLESTATUSES,
  sortByStatusFlow,
} from './helpers/orderStatusMapper';
import { getEofsToUserStatus } from '../account/sections/orders/order-page/components/OrderUtils';
import { TabsPath } from '../account/types/types';
import {
  formatISO,
  getParentLineItems,
  groupLineItemsBySku,
  separateMediaAndPrintBuilderProducts,
} from '../cart/helpers';
import { getDiscountList, getHeadingStyles } from '../utils';
import { formatDateTimeWithTimezone } from 'helpers/estimatedPickupDatehelper';

// Statuses where the stepper must NOT be shown
export const BLOCKED_STATUSES = new Set(['CANCELLED', 'REFUNDED', 'REMAKE', 'CANCELREJECTED']);

// statuses where lineitems are show together
export const initialViewStatus = new Set(['OPEN', 'REMAKE']);

const OrderDetails = ({ data }: any) => {
  const router = useRouter();
  const [isDesktopSize] = useMediaQuery(desktop);
  const [orderDetailsData, setOrderDetailsData] = useState<any>();
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { countryCode } = getLocalizationInfo(locale);
  const [showSidebarNames, setShowSidebarNames] = useState(false);
  const { accountLoading } = useContext(AccountContext);
  const [hash] = useHash();
  const { hideLoader, showLoader } = usePageLoader();
  const {
    cancelOrder,
    close,
    date,
    deliveryAddress,
    discount,
    discountApplieadLabel,
    earliestPickUpText,
    orderId,
    orderSummary,
    paymentMethod,
    pickUpAtRetailAddress,
    pickUpStore,
    price,
    quantity,
    shipToHome,
    shippingLabel,
    shippingMethod,
    status,
    subTotalItems,
    subTotalSubHeading,
    taxCalulatedLabel,
    taxLabel,
    vatForPhotobookLabel,
    vatForOtherProductsLabel,
    vatForShippingLabel,
    title,
    total,
    totalSavingsLabel,
    whoIsPickingUpTheOrder,
    details,
    shipmentInDays,
    breadcrumbl1,
    breadcrumbl2,
    freeSummary,
    payAtStore,
    accountTabs,
    paymentCardImages,
    p2rStatus,
    m2hStatus,
    partialOrder,
    confirmCTA,
    cancelOrderDec,
    cancelCTA,
    reorderBtnText,
    reorderSuccessfulToast,
    reorderFailedToast,
    reorderOutOfStockToast,
    reorderEligibilityDays,
    cancelOrderSuccessMsg,
    cancelOrderFailedMsg,
    detailsCTAmediaClip,
    extraPages,
    reasonCodes,
    items,
    productSaleLabel,
    trackingNumberText,
  } = data;

  const reorderText = {
    reorderBtnText,
    reorderSuccessfulToast,
    reorderFailedToast,
    reorderOutOfStockToast,
    reorderEligibilityDays,
  };

  const DOT_SIZE = 16;
  const DOT_OFFSET = DOT_SIZE / 2;
  const commonFontStyle = 'text-14 lg:text-16';
  const { getOrder, cancelOrder: canceloderById } = useCart();
  const [isShowModal, setIsShowModal] = useState(false);
  const { data: wishlist, addToWishlist } = useWishlist();
  const [orderIdFromUrl, setOrderIdFromUrl] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const { projectSettings } = useProjectSettings();
  const mediaclipExpireInDays = Number(projectSettings?.mediaclipExpireInDays) || 60;

  const orderDetailsLineItems = getParentLineItems(orderDetailsData?.lineItems);
  const breadcrumbRef = useRef<HTMLAnchorElement>(null);

  const { enableCartDiscountChanges } = useFeatures();

  useEffect(() => {
    if (globalThis.window === undefined) return;

    const params = new URLSearchParams(globalThis.window.location.search);
    const id = params.get('orderId');

    setOrderIdFromUrl(id);
  }, []);

  useEffect(() => {
    if (!orderIdFromUrl) return;

    showLoader();

    getOrder(orderIdFromUrl)
      .then((res) => setOrderDetailsData(res))
      .catch(console.error)
      .finally(() => {
        hideLoader();
      });
  }, [orderIdFromUrl, showLoader, hideLoader, getOrder]);

  const cart = orderDetailsData;
  const channelKey = cart?.lineItems?.[0]?.distributionChannel?.key?.split('-')?.[1]?.toLowerCase()?.trim();
  const channelName = channelKey && channelKey?.charAt?.(0)?.toUpperCase() + channelKey?.slice?.(1);

  const cartKind = classifyCartKind(orderDetailsLineItems?.[0]?.distributionChannel?.key);

  const eofsToUserStatus = getEofsToUserStatus(cartKind, p2rStatus, m2hStatus);

  if (orderDetailsData?.orderState?.toUpperCase() === 'CANCELLED') {
    initialViewStatus.add('CANCELLED');
  }

  const transactions = mapCosts({
    cart,
    currency: cart?.sum?.currencyCode,
    countryCode,
    enableCartDiscountChanges: enableCartDiscountChanges(),
  });

  const cartItems = useMemo(() => {
    if (!cart?.lineItems) return [];

    return getParentLineItems(cart.lineItems).map((item: any) => {
      const image =
        item?.imageMetadata?.urlThumb || item?.imageMetadata?.url || item?.variant?.images?.[0] || '/placeholder.png';

      const isPrintBuilder = item?.custom?.fields?.printsImageId;

      const printAttributes = getAttributeLabel(item?.variant?.attributes, 'size_pp') ?? '';
      const normalAttributes = item?.selectedVariantOptions ?? '';

      return {
        image,
        title: item?.name,
        subTitle: isPrintBuilder
          ? item?.variant?.attributes?.short_description_universal
          : item?.variant?.attributes?.description_universal,
        attributes: isPrintBuilder ? printAttributes : normalAttributes,
        oldPrice: item?.discountedPrice?.value ? CurrencyHelpers.formatForCurrency(item?.discount ?? 0, locale) : null,
        price: CurrencyHelpers.formatForCurrency(item?.price ?? 0, locale),
        qty: item?.count,
        total: CurrencyHelpers.formatForCurrency(item?.totalPrice ?? 0, locale),
      };
    });
  }, [cart?.lineItems]);

  const shipLines = useMemo(() => {
    if (cartKind !== 'm2hCart') return [];
    return cartItems.map((item: any) => ({
      label: `${item.attributes}`,
      oldPrice: item.oldPrice,
      price: item.price,
      qty: item.qty,
      total: item.total,
    }));
  }, [cartKind, cartItems]);

  const [mediaClipLineItems, printBuilderLineItems] = useMemo(() => {
    return separateMediaAndPrintBuilderProducts(cart?.lineItems ?? []);
  }, [cart?.lineItems]);

  const groupedSkus = useMemo(() => {
    const formattedMediaClipLineItems = mediaClipLineItems.map((lineItem) => ({
      ...lineItem,
      reorder: {
        quantity: lineItem?.count,
        distributionChannel: lineItem?.distributionChannel,
        productSku: lineItem?.variant?.sku,
      },
    }));
    return [...groupLineItemsBySku(...printBuilderLineItems), ...formattedMediaClipLineItems];
  }, [orderDetailsLineItems]);

  const groupedOrderStatus = useMemo(() => {
    if (initialViewStatus.has(orderDetailsData?.orderState.toUpperCase())) {
      return [
        {
          statusCode: orderDetailsData?.orderState,
          lineItems: groupedSkus,
          trackingDetails: undefined,
        },
      ];
    }
    if (cartKind === 'm2hCart') {
      return sortByStatusFlow(
        groupLineItemsWithTrackingAndStatusSplit(orderDetailsLineItems, orderDetailsData?.deliveries),
        POSSIBLESTATUSES,
      );
    } else {
      return sortByStatusFlow(groupLineItemsByOrderStatus(orderDetailsLineItems), POSSIBLESTATUSES);
    }
  }, [orderDetailsLineItems]);

  type SectionMode = 'p2rCart' | 'm2hCart';

  const sectionMode: SectionMode = cartKind === 'm2hCart' ? 'm2hCart' : 'p2rCart';

  const totalItemCount = useMemo(() => {
    return cartItems.reduce((total: number, item: any) => {
      return total + (item.qty ?? 0);
    }, 0);
  }, [cartItems]);

  const itemsSectionData = {
    title: cartKind === 'p2rCart' ? 'Pick Up at' : 'Ship to Home',
    itemCount: totalItemCount,
    mode: sectionMode,
    items: cartItems,
    shipLines,
  };

  const discounts = getDiscountList(orderDetailsData, enableCartDiscountChanges());

  const discountsContainerClassName = useClassNames([
    'mt-16 flex flex-col gap-12',
    discounts?.length === 0 ? 'hidden' : 'block',
  ]);

  useEffect(() => {
    if (orderDetailsData) return;
    breadcrumbRef.current?.focus();
  }, [orderDetailsData]);

  if (!orderDetailsData) return null;

  const normalizeGroupedItem = (item: any, locale: string) => {
    return {
      title: item.name || item?.variant?.name || '',
      subTitle:
        item?.variant?.attributes?.short_description_universal ||
        item?.variant?.attributes?.description_universal ||
        '',
      attributes:
        item?.selectedVariantOptions || getAttributeLabel(item?.variant?.attributes, 'size_pp') || getAttributeLabel(item?.variant?.attributes, 'size_wa') || '',
      price: CurrencyHelpers.formatForCurrency(item?.price ?? 0, locale),
      oldPrice: item?.discountedPrice?.value
        ? CurrencyHelpers.formatForCurrency(item.discountedPrice.value, locale)
        : null,
      qty: item?.count ?? 1,
      total: CurrencyHelpers.formatForCurrency(item?.totalPrice ?? 0, locale),
    };
  };

  const handleCancelOrder = async () => {
    try {
      // Close modal immediately
      setIsShowModal(false);

      // Show full-page loader
      showLoader();

      const mediaclipProds =
        orderDetailsLineItems?.filter(
          (item: any) =>
            typeof item?.custom?.fields?.mediaClipProjectId === 'string' &&
            item.custom.fields.mediaClipProjectId.trim().length > 0,
        ) ?? [];

      // Only add to wishlist if mediaclip products are present
      if (mediaclipProds.length > 0) {
        await Promise.all(
          mediaclipProds.map(async (product: any) => {
            const wishlistLineItem = {
              variant: {
                sku: product?.variant?.sku,
              },
              count: product?.quantity || 1,
              custom: {
                type: {
                  typeId: 'type',
                  key: 'lineitem_ext',
                },
                fields: {
                  mediaClipProjectId: product?.custom?.fields?.mediaClipProjectId,
                  expirationDate: new Date(
                    new Date().setDate(new Date().getDate() + mediaclipExpireInDays),
                  ).toISOString(),
                },
              },
            };

            return addToWishlist(wishlist as Wishlist, wishlistLineItem as any, 1);
          }),
        );
      }

      // Cancel order after wishlist save (or immediately if no mediaclip products)
      const cancelres = await canceloderById(orderDetailsData?.orderId);

      const res = await getOrder(orderIdFromUrl as any);
      setOrderDetailsData(res);

      if (cancelres?.isError) {
        toast.error(cancelOrderFailedMsg);
      } else {
        toast.success(cancelOrderSuccessMsg);
      }
    } catch (error) {
      toast.error(cancelOrderFailedMsg);
      console.error('Error in cancel order flow:', error);
    } finally {
      hideLoader();
    }
  };

  const shouldDisplayReorderButton = () => {
    const reorderEligibilityDays = data?.reorderEligibilityDays;
    const orderDate = new Date(orderDetailsData?.createdAt ?? '');
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - orderDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= reorderEligibilityDays;
  };

  const handleClose = (event: any) => {
    event.stopPropagation();

    const deliveryMethod = searchParams.get('deliveryMethod');
    const status = searchParams.get('status');
    const date = searchParams.get('date');

    const query: Record<string, string> = { hash: TabsPath.ORDERS_STATUS };

    if (deliveryMethod) query.deliveryMethod = deliveryMethod;
    if (status) query.status = status;
    if (date) query.date = date;

    router.push({
      pathname: '/account',
      query,
    });
  };

  const region = NORTH_AMERICA_LOCALES?.includes(countryCode) ? countryCode : EU_LOCALE;
  const euTimezoneWithData = formatDateTimeWithTimezone(new Date(orderDetailsData?.createdAt), locale);

  return (
    <div className="relative p-12 dark:bg-gmsb-header-gray lg:px-48 lg:pb-48" data-ffid="ctr-order-details-root">
      <div className="px-16 pt-24 md:pl-115" data-ffid="ctr-order-details-breadcrumb">
        <Breadcrumb listClassName="w-fit" data-ffid="ctr-order-details-breadcrumb-list" ref={breadcrumbRef}>
          {searchParams.get('rf') ? (
            <button
              className="flex items-center"
              onClick={() => {
                const params = new URLSearchParams(searchParams.toString());
                params.delete('rf');
                params.delete('orderId');
                params.append('hash', 'order-status');
                router.push(`/account?${params.toString()}`);
              }}
              data-ffid="ctr-order-details-breadcrumb-home"
              type='button'
            >
              <NavLeftIcon dimension={24} />
              <span data-ffid="lbl-order-details-breadcrumb-home" className="pl-5 text-14">
                {breadcrumbl1}
              </span>
            </button>
          ) : (
            <Link
              link="/account?hash=order-status"
              className="flex items-center gap-8 text-14 text-neutral-600 transition hover:text-neutral-800"
              data-ffid="ctr-order-details-breadcrumb-home"
            >
              <NavLeftIcon dimension={24} />
              <span data-ffid="lbl-order-details-breadcrumb-home" className="text-14">
                {breadcrumbl1}
              </span>
            </Link>
          )}

          <Link
            link=""
            className="text-14 text-neutral-600 transition hover:text-neutral-800"
            data-ffid="ctr-order-details-breadcrumb-orders"
          >
            {isDesktopSize && breadcrumbl2}
          </Link>

          {/* <span
            className="cursor-default text-16 font-bold text-neutral-800"
            data-ffid="lbl-order-details-breadcrumb-current"
          >
            {title}
          </span> */}
        </Breadcrumb>
      </div>

      <h1
        className="mb-24 text-center lg:block"
        data-ffid="lbl-order-details-title"
        style={getHeadingStyles(data?.pageTitleCss, isDesktopSize)}
      >
        {title}
      </h1>

      <div></div>

      <div className="flex lg:gap-24" data-ffid="ctr-order-details-main">
        <AccountTabsComponent
          text={accountTabs}
          hash={hash}
          accountLoading={accountLoading}
          showSidebarNames={showSidebarNames}
          setShowSidebarNames={setShowSidebarNames}
        />

        <div
          className="mb-88 flex flex-1 flex-col rounded-xl border border-[#EBEBEB] bg-white p-12 lg:p-32"
          data-ffid="ctr-order-details-content"
        >
          <div className="flex flex-col pb-58 lg:flex-row lg:gap-26" data-ffid="ctr-order-details-body">
            <div className="lg:w-[75%]" data-ffid="ctr-order-details-left">
              <div className="lg:block" data-ffid="ctr-order-details-left-block">
                {groupedOrderStatus.length > 0 &&
                  groupedOrderStatus.map((order, index, arr) => {
                    const orderSectionData = {
                      title: cartKind === 'p2rCart' ? 'Pick Up at' : 'Ship to Home',
                      itemCount: order?.lineItems.length,
                      mode: sectionMode,
                      items: order?.lineItems,
                      shipLines,
                    };
                    const currentStatus = order?.statusCode?.toUpperCase();
                    const orderQty = getTotalCount(order?.lineItems);
                    const currentStatusIndex = getBackendCurrentIndex(
                      currentStatus,
                      getP2RBackendTimelineSteps(currentStatus, cartKind),
                    );
                    const orderStatusSkus = order?.lineItems;
                    return (
                      <div
                        key={'order' + index}
                        data-ffid={`ctr-order-details-part-${index}`}
                        className={clsx(index > 0 && 'mt-24')}
                      >
                        <div
                          className={`text-14 font-bold lg:text-18`}
                          data-ffid={`lbl-order-details-ship-pickup-title-${index}`}
                        >
                          {cartKind === 'p2rCart' ? pickUpStore : shipToHome}{' '}
                          {arr.length > 1 && `(${index + 1} of ${arr.length})`}
                        </div>

                        <div className={commonFontStyle} data-ffid={`lbl-order-details-status-${index}`}>
                          {`${status}:  ${eofsToUserStatus[currentStatus?.toUpperCase()] ?? currentStatus}`}
                        </div>

                        <div className={commonFontStyle} data-ffid={`lbl-order-details-id-${index}`}>
                          {`${orderId}: ${orderDetailsData?.orderNumber}`}
                        </div>

                        {region === EU_LOCALE ? (
                          <div className={commonFontStyle} data-ffid={`lbl-order-details-date-${index}`}>
                            {euTimezoneWithData}
                          </div>
                        ) : (
                          <div className={commonFontStyle} data-ffid={`lbl-order-details-date-${index}`}>
                            {`${date}: ${formatISO(orderDetailsData?.createdAt, 'isConfirm')}`}
                          </div>
                        )}
                        <div
                          className="mt-24 rounded-xl border border-[#EBEBEB] bg-white"
                          data-ffid={`ctr-order-details-items-stepper-${index}`}
                        >
                          {!initialViewStatus.has(currentStatus) && (
                            <div className="flex w-full items-center justify-center rounded-t-xl bg-[#F5F5F5] py-8 text-16 font-bold text-[#333333]">
                              {!hasSameStatusCode(orderDetailsLineItems) && (partialOrder ?? 'Partial Order:')}{' '}
                              {eofsToUserStatus[currentStatus?.toUpperCase()] ?? currentStatus}
                            </div>
                          )}
                          <OrderItemsSection
                            index={index}
                            title={orderSectionData?.title}
                            itemCount={orderQty}
                            mode={orderSectionData?.mode}
                            items={orderStatusSkus}
                            shipLines={orderStatusSkus?.map((sku: any) => {
                              const normalized = normalizeGroupedItem(sku, locale);

                              return {
                                label: normalized?.attributes || normalized?.subTitle || normalized?.title,
                                price: normalized?.price,
                                oldPrice: normalized?.oldPrice,
                                qty: normalized?.qty,
                                total: normalized?.total,
                              };
                            })}
                            price={price}
                            quantity={quantity}
                            total={total}
                            details={details}
                            channelName={channelName}
                            reorderText={reorderText}
                            shouldDisplayReorderButton={shouldDisplayReorderButton()}
                            shippingAddress={orderDetailsData?.shippingAddress}
                            shippingInfo={orderDetailsData?.shippingInfo}
                            allLineItems={orderDetailsData?.lineItems}
                            extraPages={extraPages}
                            detailsCTAmediaClip={detailsCTAmediaClip}
                            reasonCodes={reasonCodes}
                          />
                          {order?.trackingDetails?.trackingId &&
                            !order?.trackingDetails?.trackingId?.toLowerCase()?.includes('invalid') && (
                              <div className="px-12 sm:px-16">
                                <div className="text-16 font-bold text-[#333333]">
                                  {trackingNumberText ?? `Tracking Number`}
                                </div>
                                <div className="text-16 font-normal">
                                  {order?.trackingDetails?.carrier && (
                                    <span className="capitalize text-[#333333]">
                                      {' '}
                                      {order?.trackingDetails?.carrier}
                                    </span>
                                  )}
                                  <span className="text-[#333333]"> {order?.trackingDetails?.trackingId}</span>
                                </div>
                              </div>
                            )}

                          {!BLOCKED_STATUSES.has(currentStatus) && (
                            <OrderStepperComponent
                              status={status}
                              statusSteps={getP2RBackendTimelineSteps(currentStatus, cartKind).map((cs) => ({
                                ...cs,
                                label: eofsToUserStatus[cs.label?.toUpperCase()],
                              }))}
                              currentStatusIndex={Math.max(currentStatusIndex, 0)}
                              DOT_OFFSET={DOT_OFFSET}
                              cartKind={cartKind}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {!isDesktopSize && (
                <OrderSummary
                  cartKind={cartKind}
                  locale={locale}
                  transactions={transactions}
                  itemsCount={itemsSectionData.itemCount}
                  orderSummary={orderSummary}
                  subTotalSubHeading={subTotalSubHeading}
                  subTotalItems={subTotalItems}
                  discount={discount}
                  shippingLabel={shippingLabel}
                  taxLabel={taxLabel}
                  vatForPhotobookLabel={vatForPhotobookLabel}
                  vatForOtherProductsLabel={vatForOtherProductsLabel}
                  vatForShippingLabel={vatForShippingLabel}
                  total={total}
                  taxCalulatedLabel={taxCalulatedLabel}
                  totalSavingsLabel={totalSavingsLabel}
                  discountApplieadLabel={discountApplieadLabel}
                  discounts={discounts}
                  discountsContainerClassName={discountsContainerClassName}
                  sectionMode={sectionMode}
                  paymentMethod={paymentMethod}
                  payAtStore={payAtStore}
                  whoIsPickingUpTheOrder={whoIsPickingUpTheOrder}
                  orderDetailsData={orderDetailsData}
                  freeSummary={freeSummary}
                  pickupFirstName={orderDetailsData?.pickupFirstName}
                  pickupLastName={orderDetailsData?.pickupLastName}
                  items={items}
                  productSaleLabel={productSaleLabel}
                />
              )}

              <OrderFulfillmentSection
                sectionMode={sectionMode}
                pickUpAtRetailAddress={pickUpAtRetailAddress}
                channelName={channelName}
                orderDetailsData={orderDetailsData}
                paymentMethod={paymentMethod}
                deliveryAddress={deliveryAddress}
                shippingMethod={shippingMethod}
                estimateDateHomePickup={orderDetailsData?.pickupdatetime}
                locale={locale}
                shipmentInDays={shipmentInDays}
                earliestPickUpText={earliestPickUpText}
                transactions={transactions}
                paymentCardImages={paymentCardImages}
                freeSummary={freeSummary}
              />
            </div>

            {isDesktopSize && (
              <OrderSummary
                cartKind={cartKind}
                locale={locale}
                transactions={transactions}
                itemsCount={itemsSectionData.itemCount}
                orderSummary={orderSummary}
                subTotalSubHeading={subTotalSubHeading}
                subTotalItems={subTotalItems}
                discount={discount}
                shippingLabel={shippingLabel}
                taxLabel={taxLabel}
                vatForPhotobookLabel={vatForPhotobookLabel}
                vatForOtherProductsLabel={vatForOtherProductsLabel}
                vatForShippingLabel={vatForShippingLabel}
                total={total}
                taxCalulatedLabel={taxCalulatedLabel}
                totalSavingsLabel={totalSavingsLabel}
                discountApplieadLabel={discountApplieadLabel}
                discounts={discounts}
                discountsContainerClassName={discountsContainerClassName}
                sectionMode={sectionMode}
                paymentMethod={paymentMethod}
                whoIsPickingUpTheOrder={whoIsPickingUpTheOrder}
                payAtStore={payAtStore}
                orderDetailsData={orderDetailsData}
                freeSummary={freeSummary}
                pickupFirstName={orderDetailsData?.pickupFirstName}
                pickupLastName={orderDetailsData?.pickupLastName}
                items={items}
                productSaleLabel={productSaleLabel}
              />
            )}
          </div>

          <div
            className={clsx(
              'fixed bottom-0 left-0 z-30 w-full max-w-full bg-white px-24 py-20 shadow-[0px_-5px_8px_0px_#0000000D]',
              'flex flex-col gap-16',
              'sm:flex-row sm:justify-end sm:gap-12 sm:px-24',
              'lg:px-20 xl:px-76',
            )}
            data-ffid="ctr-order-details-footer"
          >
            <button
              type="button"
              onClick={(e) => handleClose(e)}
              className="w-full rounded-[34px] border-2 border-green-800 px-34 py-12 text-14 text-green-800 hover:bg-green-800 hover:text-white sm:w-200 sm:text-16"
              data-ffid="btn-order-details-cancel"
            >
              {close}
            </button>

            {orderDetailsData?.orderState?.toLowerCase() === 'open' && cartKind === 'm2hCart' && (
              <button
                type="button"
                onClick={() => setIsShowModal(true)}
                className="w-full rounded-[34px] border-2 border-green-800 px-34 py-12 text-14 text-green-800 hover:bg-green-800 hover:text-white disabled:cursor-not-allowed disabled:border-[#D6D6D6] disabled:bg-[#D6D6D6] disabled:text-[#999999] sm:ml-12 sm:w-200 sm:text-16"
                data-ffid="btn-order-details-close"
              >
                {cancelOrder}
              </button>
            )}
          </div>
        </div>
      </div>

      <ModalPopUp
        isOpen={isShowModal}
        onClose={() => setIsShowModal(false)}
        title={cancelOrder}
        messageDescription={cancelOrderDec}
        ffidValue="cancel-order-modal"
        showCancelButton
        cancelText={cancelCTA}
        onCancel={() => {
          setIsShowModal(false);
        }}
        actionType="close"
        actionText={confirmCTA}
        onAction={handleCancelOrder}
      />
    </div>
  );
};

export default OrderDetails;
